import React from 'react'
import {LVS_COLORS, type LvsColorKey} from '../lvs-colors'
import type {Painter, Point, PathOp, LvsColorMode} from './types'

function pathOpsToD(ops: readonly PathOp[]): string {
    const parts: string[] = []
    for (const o of ops) {
        if (o.op === 'm') parts.push(`M${o.x} ${o.y}`)
        else if (o.op === 'l') parts.push(`L${o.x} ${o.y}`)
        else if (o.op === 'c') {
            parts.push(`C${o.c1x} ${o.c1y} ${o.c2x} ${o.c2y} ${o.x} ${o.y}`)
        } else if (o.op === 'h') parts.push('Z')
    }
    return parts.join('')
}

/**
 * Painter adapter that accumulates React.ReactNode children. Two
 * consumers:
 *   - on-screen React components (Arrow, SignBody) instantiate
 *     `SvgPainter('screen', idSuffix)`, paint into it, and return
 *     `painter.children()` inside a fragment.
 *   - export-to-SVG instantiates `SvgPainter('print', idSuffix)`,
 *     paints, then renders the whole tree via renderToStaticMarkup.
 *
 * Print mode keeps the sRGB hex in `fill=`/`stroke=` so browsers display
 * approximately the right color, and adds an inline `style` declaration
 * with `device-cmyk(...)` so press tools read CMYK. Screen mode emits
 * hex only.
 */
export class SvgPainter implements Painter {
    private readonly nodeStack: React.ReactNode[][] = [[]]
    private readonly defs: React.ReactNode[] = []
    private nextKey = 0
    private nextClipId = 0

    constructor(
        readonly mode: LvsColorMode,
        readonly idSuffix: string
    ) {}

    private push(node: React.ReactNode): void {
        this.nodeStack[this.nodeStack.length - 1].push(node)
    }

    private k(): string {
        return `n-${this.idSuffix}-${this.nextKey++}`
    }

    private fillAttrs(key: LvsColorKey): {fill: string; style?: React.CSSProperties} {
        const c = LVS_COLORS[key]
        if (this.mode === 'print') {
            return {fill: c.hex, style: {fill: formatDeviceCmyk(c.cmyk100)}}
        }
        return {fill: c.hex}
    }

    private strokeAttrs(
        key: LvsColorKey,
        lineWidth: number
    ): {stroke: string; strokeWidth: number; style?: React.CSSProperties} {
        const c = LVS_COLORS[key]
        if (this.mode === 'print') {
            return {
                stroke: c.hex,
                strokeWidth: lineWidth,
                style: {stroke: formatDeviceCmyk(c.cmyk100)}
            }
        }
        return {stroke: c.hex, strokeWidth: lineWidth}
    }

    fillRoundedRect(
        fill: LvsColorKey,
        x: number,
        y: number,
        w: number,
        h: number,
        r: number
    ): void {
        const safeR = Math.max(0, r)
        this.push(
            React.createElement('rect', {
                key: this.k(),
                x,
                y,
                width: w,
                height: h,
                rx: safeR,
                ...this.fillAttrs(fill)
            })
        )
    }

    fillPolygon(fill: LvsColorKey, verts: readonly Point[]): void {
        if (verts.length === 0) return
        const points = verts.map(v => `${v.x},${v.y}`).join(' ')
        this.push(
            React.createElement('polygon', {
                key: this.k(),
                points,
                fillRule: 'nonzero',
                ...this.fillAttrs(fill)
            })
        )
    }

    fillPathOps(fill: LvsColorKey, ops: readonly PathOp[]): void {
        if (ops.length === 0) return
        const d = pathOpsToD(ops)
        this.push(
            React.createElement('path', {
                key: this.k(),
                d,
                fillRule: 'evenodd',
                ...this.fillAttrs(fill)
            })
        )
    }

    strokeLine(
        stroke: LvsColorKey,
        lineWidth: number,
        x1: number,
        y1: number,
        x2: number,
        y2: number
    ): void {
        this.push(
            React.createElement('line', {
                key: this.k(),
                x1,
                y1,
                x2,
                y2,
                ...this.strokeAttrs(stroke, lineWidth)
            })
        )
    }

    withClipRoundedRect(
        x: number,
        y: number,
        w: number,
        h: number,
        r: number,
        body: () => void
    ): void {
        const safeR = Math.max(0, r)
        const id = `clip-${this.idSuffix}-${this.nextClipId++}`
        this.defs.push(
            React.createElement(
                'clipPath',
                {key: id, id},
                React.createElement('rect', {x, y, width: w, height: h, rx: safeR})
            )
        )
        const clippedNodes: React.ReactNode[] = []
        this.nodeStack.push(clippedNodes)
        try {
            body()
        } finally {
            this.nodeStack.pop()
            this.push(
                React.createElement('g', {key: this.k(), clipPath: `url(#${id})`}, ...clippedNodes)
            )
        }
    }

    /** Returns the accumulated children. Caller wraps in <svg>. */
    children(): React.ReactNode[] {
        if (this.defs.length === 0) return this.nodeStack[0]
        return [React.createElement('defs', {key: 'defs'}, ...this.defs), ...this.nodeStack[0]]
    }
}

function formatDeviceCmyk(cmyk: readonly [number, number, number, number]): string {
    const fmt = (n: number): string => {
        const s = n.toFixed(4)
        return s.includes('.') ? s.replace(/\.?0+$/, '') : s
    }
    return `device-cmyk(${fmt(cmyk[0])}, ${fmt(cmyk[1])}, ${fmt(cmyk[2])}, ${fmt(cmyk[3])})`
}
